import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import * as Sentry from "@sentry/browser";
import useFirebase from "../hooks/useFirebase";
import { isLoggedIn, isPremium } from "../services/auth";
import { videoDownload } from "../services/videos";
import { getSignedDownloadUrl } from "../services/cloudflareWorker";

// Animated ellipses component
const AnimatedEllipsis = () => {
  const [dots, setDots] = useState('.');

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prevDots => {
        if (prevDots === '.') return '..';
        if (prevDots === '..') return '...';
        return '.';
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Use a fixed-width container to prevent layout shifts during animation
  return <span className="inline-block w-[18px] text-left">{dots}</span>;
};

// Error popup component to display worker error messages
const ErrorPopup = ({ message, onClose }) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 max-w-md mx-auto shadow-xl">
        <div className="flex flex-col items-center">
          <div className="text-red-600 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Download Error</h3>
          <p className="text-center text-gray-600 mb-6">{message}</p>
          <button
            onClick={onClose}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const Buttons = ({ vid, premium_4k, premium_lg, premium_sm }) => {
  const [loadingUhd, setLoadingUhd] = useState(false);
  const [loadingHd, setLoadingHd] = useState(false);
  const [loadingSd, setLoadingSd] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [downloadingFormat, setDownloadingFormat] = useState("");
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  useFirebase();

  const downloadVideo = async (path, format) => {
    if (!isLoggedIn() || !isPremium()) return;
    let linkText = 'Download HD';

    // Set loading state based on format
    if (format === "uhd") {
      setLoadingUhd(true);
      setDownloadingFormat("4K");
      linkText = 'Download 4k';
    } else if (format === "hd") {
      setLoadingHd(true);
      setDownloadingFormat("HD");
      linkText = 'Download HD';
    } else if (format === "sd") {
      setLoadingSd(true);
      setDownloadingFormat("SD");
      linkText = 'Download SD';
    }

    // Set a timeout to clear the loading state after seconds
    // This will be overridden if the download starts sooner
    const timeoutId = setTimeout(() => {
      setLoadingUhd(false);
      setLoadingHd(false);
      setLoadingSd(false);
      setDownloadingFormat("");
      setDownloadStarted(false);
    }, 20000);

    // Set a timeout to report slow downloads to Sentry after 3 seconds
    const sentryTimeoutId = setTimeout(() => {
      // Only report if download hasn't started yet
      if (!downloadStarted) {
        Sentry.captureMessage(
          `Slow download detected for format: ${format}, video: ${vid}`,
          'warning'
        );
      }
    }, 3000);

    try {
      // Call the Cloudflare worker directly
      const currentDownload = vid;

      const splitPath = path.split('/')
      const fileName = `${splitPath[splitPath.length - 1]}.mp4`;

      // Clear the timeouts when we get a response
      clearTimeout(timeoutId);
      clearTimeout(sentryTimeoutId);

      const url = await getSignedDownloadUrl({ currentDownload, video: `${path}.mp4`, fileName });
      videoDownload({ fileExtension: 'mp4', fileName, linkUrl: `${path}.mp4`, linkText })

      // Mark download as started
      setDownloadStarted(true);

      // Keep the loading state for 1 second after download starts
      setTimeout(() => {
        setLoadingUhd(false);
        setLoadingHd(false);
        setLoadingSd(false);
        setDownloadingFormat("");
        setDownloadStarted(false);
      }, 1000);

      // Redirect to download link
      window.location.href = url;
    } catch (error) {
      console.error("Download error:", error);
      // Report the error to Sentry
      Sentry.captureException(error);
      // Clear the Sentry timeout since we're already reporting the error
      clearTimeout(sentryTimeoutId);
      // Reset loading states in case of error
      setLoadingUhd(false);
      setLoadingHd(false);
      setLoadingSd(false);
      setDownloadingFormat("");
      setDownloadStarted(false);

      // Show error popup with the message from the worker
      setErrorMessage(error.message || "An error occurred while trying to download the video. Please try again later.");
      setShowError(true);
    }
  }

  if (isPremium()) {
    // Count how many buttons we have to determine width
    // eslint-disable-next-line no-unused-vars
    const buttonCount = [premium_4k, premium_lg, premium_sm].filter(Boolean).length;

    // Width classes are now applied directly in the component markup
    // Removing unused variable
    // const _widthClass = buttonCount === 1 ? 'w-full' :
    //                   buttonCount === 2 ? 'w-1/2' : 'w-1/3';

    return(
      <div className="buttons flex w-full md:content-end mt-2 md:mt-8 justify-center md:justify-end relative">
        {/* Error popup */}
        {showError && (
          <ErrorPopup
            message={errorMessage}
            onClose={() => setShowError(false)}
          />
        )}

        {/* Overlay for downloading message */}
        {(loadingUhd || loadingHd || loadingSd) && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-r from-indigo-600 to-indigo-800 z-10">
            <div className="text-white font-medium">
              <span>Your download will start shortly<AnimatedEllipsis /></span>
            </div>
          </div>
        )}
        <div className="flex flex-row justify-center w-full space-x-1">
          {premium_4k && (
            <div className="flex-1">
              <button
                className="w-full flex h-10 md:h-12 px-1 md:px-[0.2rem] items-center justify-center text-white transition-all duration-300 ease-in-out bg-gradient-to-r from-indigo-600 to-indigo-800 hover:from-indigo-700 hover:to-indigo-900 focus:shadow-outline shadow-md disabled:cursor-not-allowed"
                onClick={() => downloadVideo(premium_4k, "uhd")}
                disabled={loadingUhd || loadingHd || loadingSd}
                aria-label="Download 4K Video"
              >
                <div className="flex items-center justify-center w-full">
                  <svg className="h-4 w-4 md:h-5 md:w-5 mr-1 flex-shrink-0" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M8 17V15H16V17H8M16 10L12 14L8 10H10.5V6H13.5V10H16M12 2C17.5 2 22 6.5 22 12C22 17.5 17.5
                      22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2M12 4C7.58 4 4 7.58 4 12C4 16.42 7.58 20 12 20C16.42 20 20 16.42 20
                      12C20 7.58 16.42 4 12 4Z" />
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 md:h-5 md:w-5 mr-1 flex-shrink-0" viewBox="0 0 24 24">
                    <path fill="currentColor"
                      d="M19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M12,13.5H11V15H9.5V13.5H6.5V9H8V12H9.5V9H11V12H12V13.5M18,15H16.2L14.4,12.8V15H13V9H14.5V11.2L16.2,9H18L15.8,12L18,15Z"
                    />
                  </svg>
                  <span className="text-xs whitespace-nowrap">4K</span>
                </div>
              </button>
            </div>
          )}

          {premium_lg && (
            <div className="flex-1">
              <button
                className="w-full flex h-10 md:h-12 px-1 md:px-[0.2rem] items-center justify-center text-white transition-all duration-300 ease-in-out bg-gradient-to-r from-indigo-600 to-indigo-800 hover:from-indigo-700 hover:to-indigo-900 focus:shadow-outline shadow-md disabled:cursor-not-allowed"
                onClick={() => downloadVideo(premium_lg, "hd")}
                disabled={loadingUhd || loadingHd || loadingSd}
                aria-label="Download HD Video"
              >
                <div className="flex items-center justify-center w-full">
                  <svg className="h-4 w-4 md:h-5 md:w-5 mr-1 flex-shrink-0" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M8 17V15H16V17H8M16 10L12 14L8 10H10.5V6H13.5V10H16M12 2C17.5 2 22 6.5 22 12C22 17.5 17.5
                      22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2M12 4C7.58 4 4 7.58 4 12C4 16.42 7.58 20 12 20C16.42 20 20 16.42 20
                      12C20 7.58 16.42 4 12 4Z" />
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 md:h-5 md:w-5 mr-1 flex-shrink-0" viewBox="0 0 24 24">
                    <path fill="currentColor"
                      d="M18,9H16V7H18M18,13H16V11H18M18,17H16V15H18M8,9H6V7H8M8,13H6V11H8M8,17H6V15H8M18,3V5H16V3H8V5H6V3H4V21H6V19H8V21H16V19H18V21H20V3H18Z"
                    />
                  </svg>
                  <span className="text-xs whitespace-nowrap">HD</span>
                </div>
              </button>
            </div>
          )}

          {premium_sm && (
            <div className="flex-1">
              <button
                className="w-full flex h-10 md:h-12 px-1 md:px-[0.2rem] items-center justify-center text-white transition-all duration-300 ease-in-out bg-gradient-to-r from-indigo-600 to-indigo-800 hover:from-indigo-700 hover:to-indigo-900 focus:shadow-outline shadow-md disabled:cursor-not-allowed"
                onClick={() => downloadVideo(premium_sm, "sd")}
                disabled={loadingUhd || loadingHd || loadingSd}
                aria-label="Download SD Video"
              >
                <div className="flex items-center justify-center w-full">
                  <svg className="h-4 w-4 md:h-5 md:w-5 mr-1 flex-shrink-0" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M8 17V15H16V17H8M16 10L12 14L8 10H10.5V6H13.5V10H16M12 2C17.5 2 22 6.5 22 12C22 17.5 17.5
                      22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2M12 4C7.58 4 4 7.58 4 12C4 16.42 7.58 20 12 20C16.42 20 20 16.42 20
                      12C20 7.58 16.42 4 12 4Z" />
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 md:h-5 md:w-5 mr-1 flex-shrink-0" viewBox="0 0 24 24">
                    <path fill="currentColor"
                      d="M17,19H7V5H17M17,1H7C5.89,1 5,1.89 5,3V21A2,2 0 0,0 7,23H17A2,2 0 0,0 19,21V3C19,1.89 18.1,1 17,1Z"
                    />
                  </svg>
                  <span className="text-xs whitespace-nowrap">SD</span>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    )
  } else {
    return(<span />)
  }
}

Buttons.propTypes = {
  vid: PropTypes.string.isRequired,
  premium_4k: PropTypes.string,
  premium_lg: PropTypes.string.isRequired,
  premium_sm: PropTypes.string,
}

export default Buttons
